import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getUserStatsApi, getAdminStatsApi, type StatsParams } from '@/api/dashboard'
import { useAuthStore } from '@/stores/authStore'

export interface RealtimeRpmResult {
  rpm: number
  updatedAt: number
}

export interface RealtimeRpmOptions {
  tokenId?: number | null
}

/**
 * 近 60 秒请求数（RPM），来自 GET /api/log/self/stat 或管理员 GET /api/log/stat。
 * 后端在 logs 表上按 created_at >= now-60s 统计 count(*)，非 quota_data 估算。
 */
export function useRealtimeRpm(options?: RealtimeRpmOptions) {
  const isAdmin = useAuthStore((state) => state.user?.role ?? 0) >= 10
  const tokenId = options?.tokenId

  const statParams: StatsParams | undefined = useMemo(() => {
    if (tokenId == null || tokenId <= 0) return undefined
    return { token_id: tokenId }
  }, [tokenId])

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['realtimeRpm', isAdmin, statParams?.token_id],
    queryFn: async (): Promise<RealtimeRpmResult> => {
      const stat = isAdmin ? await getAdminStatsApi(statParams) : await getUserStatsApi(statParams)
      return {
        rpm: stat.rpm ?? 0,
        updatedAt: Date.now(),
      }
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  return {
    rpm: data?.rpm,
    updatedAt: data?.updatedAt ?? (dataUpdatedAt > 0 ? dataUpdatedAt : undefined),
    isLoading,
    isError,
  }
}
